import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box, Typography, Rating } from "@mui/material";
import { ContentCopyOutlined, TableChartOutlined, DescriptionOutlined, PictureAsPdfOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import ReviewsApi from "Services/Reviews";

const Reviews = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  const normalizeReviewsList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.data?.reviews)) {
      return { rows: payload.data.reviews || [], total: payload?.data?.total || payload?.data?.reviews?.length || 0 };
    }
    if (Array.isArray(payload?.reviews)) {
      return { rows: payload.reviews || [], total: payload?.total || payload?.reviews?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getReviews = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    ReviewsApi.getReviewsList({ page, limit: pageSize }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeReviewsList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize]);

  const getAllReviews = useCallback(() => {
    ReviewsApi.getReviewsList({ page: 1, limit: 1000 }).then(
      (res) => {
        const normalized = normalizeReviewsList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getReviews();
    getAllReviews();
  }, [getReviews, getAllReviews]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = _.map(row || [], (data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    bookingId: data?.booking_id || data?.bookings?.id || "-",
    reviewer: `${data?.users_reviews_reviewer_idTousers?.first_name || ""} ${data?.users_reviews_reviewer_idTousers?.last_name || ""}`.trim() || data?.reviewer_id || "-",
    reviewee: `${data?.users_reviews_reviewee_idTousers?.first_name || ""} ${data?.users_reviews_reviewee_idTousers?.last_name || ""}`.trim() || data?.reviewee_id || "-",
    rating: Number(data?.rating || 0),
    comment: data?.comment || "No Comment",
    driverReview: data?.is_driver_review ? "Yes" : "No",
    moderationStatus: data?.moderation_status || "-",
    bookingStatus: data?.bookings?.status || "-",
    createdAt: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
  }));

  const formatteddatas = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Booking ID": r.bookingId,
    Reviewer: r.reviewer,
    Reviewee: r.reviewee,
    Rating: r.rating,
    Comment: r.comment,
    "Driver Review": r.driverReview,
    "Moderation Status": r.moderationStatus,
    "Booking Status": r.bookingStatus,
    Created: r.createdAt,
  }));

  const bulkdataRows = _.map(_.orderBy(bulkrow || [], ["created_at"], ["asc"]), (data, index) => ({
    sno: index + 1,
    bookingId: data?.booking_id || data?.bookings?.id || "-",
    reviewer: `${data?.users_reviews_reviewer_idTousers?.first_name || ""} ${data?.users_reviews_reviewer_idTousers?.last_name || ""}`.trim() || data?.reviewer_id || "-",
    reviewee: `${data?.users_reviews_reviewee_idTousers?.first_name || ""} ${data?.users_reviews_reviewee_idTousers?.last_name || ""}`.trim() || data?.reviewee_id || "-",
    rating: Number(data?.rating || 0),
    comment: data?.comment || "No Comment",
    driverReview: data?.is_driver_review ? "Yes" : "No",
    moderationStatus: data?.moderation_status || "-",
    bookingStatus: data?.bookings?.status || "-",
    createdAt: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
  }));

  const bulkformatteddatas = bulkdataRows.map((r) => ({
    "S.NO": r.sno,
    "Booking ID": r.bookingId,
    Reviewer: r.reviewer,
    Reviewee: r.reviewee,
    Rating: r.rating,
    Comment: r.comment,
    "Driver Review": r.driverReview,
    "Moderation Status": r.moderationStatus,
    "Booking Status": r.bookingStatus,
    Created: r.createdAt,
  }));

  const handlePageChange = (newPage) => {
    const theNewPage = parseInt(newPage, 10) + 1;
    if (theNewPage >= 1) setPage(theNewPage);
  };

  const handleCopy = () => {
    const header = Object.keys(formatteddatas[0] || {}).join("\t");
    const rows = formatteddatas.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };

  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatteddatas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reviews");
    XLSX.writeFile(workbook, "Reviews.xlsx");
  };

  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Reviews.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const tableData = dataRows.map((r) => [
      r.sno,
      r.bookingId,
      r.reviewer,
      r.reviewee,
      r.rating,
      r.comment,
      r.driverReview,
      r.moderationStatus,
      r.bookingStatus,
      r.createdAt,
    ]);
    doc.autoTable({
      head: [["S.NO", "Booking ID", "Reviewer", "Reviewee", "Rating", "Comment", "Driver Review", "Moderation", "Booking Status", "Created"]],
      body: tableData,
      styles: { fontSize: 8 },
    });
    doc.save("Reviews.pdf");
  };

  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkformatteddatas);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All Reviews.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      minWidth: 70,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => <Typography variant="body2">{params?.row?.sno}</Typography>,
    },
    { field: "bookingId", headerName: "Booking ID", minWidth: 220, sortable: true },
    { field: "reviewer", headerName: "Reviewer", minWidth: 170, sortable: true },
    { field: "reviewee", headerName: "Reviewee", minWidth: 170, sortable: true },
    {
      field: "rating",
      headerName: "Rating",
      minWidth: 120,
      sortable: true,
      renderCell: (params) => <Rating name="read-only" value={Number(params?.row?.rating) || 0} readOnly precision={0.5} size="small" />,
    },
    { field: "comment", headerName: "Comment", minWidth: 220, sortable: false },
    { field: "driverReview", headerName: "Driver Review", minWidth: 130, sortable: true },
    { field: "moderationStatus", headerName: "Moderation", minWidth: 130, sortable: true },
    { field: "bookingStatus", headerName: "Booking Status", minWidth: 130, sortable: true },
    { field: "createdAt", headerName: "Created", minWidth: 180, sortable: true },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} lg={1} xl={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} lg={2} xl={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={7} md={7} lg={7} xl={7} className={classes.addBtn}></Grid>

      <Grid item xs={12} sx={{ margin: "26px 0px" }}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => handlePageChange(newPage)}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={70}
            style={{ padding: "10px", borderRadius: "0px" }}
            paginationMode="server"
            rowCount={totalPages}
          />
        </section>
      </Grid>
    </Grid>
  );
};

export default Reviews;
